"use strict";
"use client";
import { jsx, Fragment, jsxs } from "react/jsx-runtime";
import { forwardRef, useRef, useState, useEffect, useCallback } from "react";
import { applyMultipleRefs } from "../../util/refs.js";
import dialogPolyfill from "../../vendor/dialog-polyfill/index.js";
import { useScrollLock } from "../../hooks/useScrollLock/useScrollLock.js";
import { clsx } from "../../styles/clsx.js";
import { useI18n } from "../../hooks/useI18n/useI18n.js";
import classes from "./Dialog.module.css.js";
import { translations } from "./translations/index.js";
import { useLatest } from "../../hooks/useLatest/useLatest.js";
import { useEscapeKey } from "../../hooks/useEscapeKey/useEscapeKey.js";
import { useClickOutside } from "../../hooks/useClickOutside/useClickOutside.js";
import { CloseButton } from "../CloseButton/CloseButton.js";
const Dialog = forwardRef(
  (props, ref) => {
    const {
      open,
      isModal = false,
      children,
      onCloseEnd,
      closeButtonLabel,
      className,
      preventOutsideClickRefs,
      preventOutsideClickClose = false,
      hideCloseButton = false,
      preventEscapeKeyClose = false,
      animationDuration = 0,
      onCloseStart,
      locale,
      style,
      ...rest
    } = useI18n(props, translations);
    const dialogRef = useRef(null);
    const openRef = useLatest(open);
    const isModalRef = useLatest(isModal);
    const animationDurationRef = useLatest(animationDuration);
    const lastFocusedElementRef = useRef(null);
    const [isClosing, setIsClosing] = useState(false);
    useEffect(() => {
      if (open) {
        if (document.activeElement instanceof HTMLElement) {
          lastFocusedElementRef.current = document.activeElement;
        }
      }
      return () => {
        var _a;
        if (!((_a = dialogRef.current) == null ? void 0 : _a.contains(document.activeElement)) && document.body !== document.activeElement) {
          return;
        }
        if (lastFocusedElementRef.current) {
          setTimeout(
            () => {
              var _a2;
              return (_a2 = lastFocusedElementRef.current) == null ? void 0 : _a2.focus();
            },
            animationDurationRef.current
          );
        }
      };
    }, [open, animationDurationRef]);
    const handleDialogClose = useCallback(() => {
      const dialogElement = dialogRef.current;
      if (!dialogElement) {
        return;
      }
      onCloseStart == null ? void 0 : onCloseStart();
      setIsClosing(true);
      setTimeout(() => {
        if (dialogElement.open) {
          dialogElement.close();
          setIsClosing(false);
        }
      }, animationDurationRef.current);
    }, [animationDurationRef, onCloseStart]);
    useEffect(() => {
      if (dialogRef.current) {
        dialogPolyfill.registerDialog(dialogRef.current);
      }
    }, []);
    useEffect(() => {
      const dialogElement = dialogRef.current;
      if (!dialogElement) {
        return void 0;
      }
      if (!openRef.current) {
        dialogElement.returnValue = "";
      }
      if (openRef.current && !dialogElement.open) {
        if (isModal) {
          dialogElement.showModal();
        } else {
          dialogElement.show();
        }
      }
      return () => {
        if (dialogElement.open) {
          dialogElement.close("skipOnClose");
        }
      };
    }, [isModal, openRef]);
    useEffect(() => {
      const dialogElement = dialogRef.current;
      if (!dialogElement) {
        return;
      }
      if (!open) {
        dialogElement.returnValue = "";
      }
      if (open && !dialogElement.open) {
        if (isModalRef.current) {
          dialogElement.showModal();
        } else {
          dialogElement.show();
        }
      }
    }, [isModalRef, open]);
    useEffect(() => {
      const dialogElement = dialogRef.current;
      if (!dialogElement) {
        return;
      }
      if (!open && dialogElement.open) {
        handleDialogClose();
      }
    }, [open, handleDialogClose]);
    useEffect(() => {
      const dialogElement = dialogRef.current;
      if (!dialogElement) {
        return void 0;
      }
      const handleClose = () => {
        if ((dialogElement == null ? void 0 : dialogElement.returnValue) !== "skipOnClose") {
          onCloseEnd == null ? void 0 : onCloseEnd();
        }
        if ((dialogElement == null ? void 0 : dialogElement.returnValue) === "skipOnClose") {
          dialogElement.returnValue = "";
        }
      };
      if (onCloseEnd) {
        dialogElement.addEventListener("close", handleClose);
      }
      return () => {
        dialogElement.removeEventListener("close", handleClose);
      };
    }, [onCloseEnd]);
    useScrollLock(open && isModal);
    useEffect(() => {
      const dialogElement = dialogRef.current;
      if (!preventEscapeKeyClose) {
        return void 0;
      }
      const handleCancel = (event) => {
        event.preventDefault();
      };
      if (dialogElement) {
        dialogElement.addEventListener("cancel", handleCancel);
      }
      return () => dialogElement == null ? void 0 : dialogElement.removeEventListener("cancel", handleCancel);
    }, [preventEscapeKeyClose]);
    const handleEscapeKey = useCallback(
      (e) => {
        var _a;
        const isTopMostDialog = e.composedPath().filter(
          (el) => el instanceof Node && el.nodeName === "DIALOG"
        )[0] === dialogRef.current;
        if (
          // if the event happened on another dialog element that is not a child of the current dialog, we don't want to close it
          !((_a = dialogRef.current) == null ? void 0 : _a.contains(e.target)) || // if the dialog is not the topmost dialog, we don't want to close it
          !isTopMostDialog
        ) {
          return;
        }
        e.preventDefault();
        handleDialogClose();
      },
      [handleDialogClose]
    );
    useEscapeKey(handleEscapeKey, open && !preventEscapeKeyClose);
    useEffect(
      () => () => {
        var _a, _b;
        if ((_a = dialogRef.current) == null ? void 0 : _a.open) {
          (_b = dialogRef.current) == null ? void 0 : _b.close();
        }
      },
      []
    );
    const handleOutsideClick = useCallback(() => {
      lastFocusedElementRef.current = null;
      handleDialogClose();
    }, [handleDialogClose]);
    const useClickOutsideRefs = preventOutsideClickRefs ? (
      // eslint-disable-next-line compat/compat
      [dialogRef, preventOutsideClickRefs].flat()
    ) : [dialogRef];
    useClickOutside(
      useClickOutsideRefs,
      handleOutsideClick,
      open && !preventOutsideClickClose
    );
    const onPolyfillBackdropClick = useCallback(
      (event) => {
        if (preventOutsideClickClose) {
          event.preventDefault();
        }
      },
      [preventOutsideClickClose]
    );
    useEffect(() => {
      const hasNativeDialog = window.HTMLDialogElement !== void 0;
      const dialogElement = dialogRef.current;
      if (!dialogElement || hasNativeDialog) {
        return void 0;
      }
      if (open && !hasNativeDialog && (dialogElement == null ? void 0 : dialogElement.nextSibling)) {
        dialogElement.nextSibling.classList.add(
          classes.backdrop
        );
        dialogElement.nextSibling.addEventListener(
          "click",
          onPolyfillBackdropClick
        );
      }
      return () => {
        var _a;
        (_a = dialogElement == null ? void 0 : dialogElement.nextSibling) == null ? void 0 : _a.removeEventListener(
          "click",
          onPolyfillBackdropClick
        );
      };
    }, [open, onPolyfillBackdropClick]);
    const onDialogClick = useCallback(
      (event) => {
        var _a, _b;
        if (!preventOutsideClickClose) {
          const isTargetChildNode = (_a = dialogRef.current) == null ? void 0 : _a.contains(
            event.target
          );
          let isWithingDialogBoundingRect = false;
          const rect = (_b = dialogRef.current) == null ? void 0 : _b.getBoundingClientRect();
          if (rect) {
            isWithingDialogBoundingRect = rect.top <= event.clientY && event.clientY <= rect.top + rect.height && rect.left <= event.clientX && event.clientX <= rect.left + rect.width;
          }
          const isBackdropClick = event.target === dialogRef.current && !isWithingDialogBoundingRect;
          if (
            // if the click happened outside the dialog by an element that is not a child of the dialog
            !isWithingDialogBoundingRect && !isTargetChildNode || // if the click happened on the backdrop
            isBackdropClick
          ) {
            handleDialogClose();
          }
        }
      },
      [handleDialogClose, preventOutsideClickClose]
    );
    return /* @__PURE__ */ jsx(Fragment, { children: /* @__PURE__ */ jsxs(
      "dialog",
      {
        onClick: onDialogClick,
        className: clsx(classes.base, isModal && classes.modal, className),
        ref: applyMultipleRefs(ref, dialogRef),
        style: {
          ...style,
          "--dialog-animation-duration": `${animationDuration}ms`
        },
        ...rest,
        children: [
          (open || isClosing) && (typeof children === "function" ? children == null ? void 0 : children({ onClose: onCloseEnd }) : children),
          !hideCloseButton && /* @__PURE__ */ jsx(CloseButton, { onClick: handleDialogClose, className: classes.close, children: closeButtonLabel })
        ]
      }
    ) });
  }
);
export {
  Dialog
};
